import React from 'react';
import { Shield, Tag, Database, Globe, Calendar, Filter } from 'lucide-react';
import { SEVERITY_OPTIONS, STATUS_OPTIONS, SeverityType, StatusType } from '@/lib/api/types';
import CustomFilter, { FilterConfig } from '@/components/common/ui/CustomFilter';

export interface SavedFilters {
    search: string;
    severity: SeverityType | 'all';
    status: StatusType | 'all';
    data_type: string;
    source: string;
    dateRange: 'all' | '7d' | '30d' | '90d';
}

export const defaultFilters: SavedFilters = {
    search: '',
    severity: 'all',
    status: 'all',
    data_type: 'all',
    source: 'all',
    dateRange: 'all'
};

interface ThreatFiltersProps {
    filters: SavedFilters;
    onFilterChange: <K extends keyof SavedFilters>(key: K, value: SavedFilters[K]) => void;
    onResetFilters: () => void;
    showAdvancedFilters: boolean;
    onToggleAdvancedFilters: () => void;
    hasActiveFilters: boolean;
}

const ThreatFilters: React.FC<ThreatFiltersProps> = ({
    filters,
    onFilterChange,
    onResetFilters,
    showAdvancedFilters,
    onToggleAdvancedFilters,
    hasActiveFilters
}) => {
    // Main filter configurations
    const mainFilters: FilterConfig[] = [
        {
            key: 'search',
            label: 'TARGET QUERY',
            type: 'search',
            placeholder: 'Search by URL, username, hostname...',
            width: 'w-64'
        },
        {
            key: 'severity',
            label: 'SEVERITY LEVEL',
            type: 'select',
            icon: <Shield className="h-3.5 w-3.5 text-[#FF3333]" />,
            options: [
                { value: 'all', label: 'All Severities' },
                ...SEVERITY_OPTIONS
            ],
            width: 'w-48'
        },
        {
            key: 'status',
            label: 'SYS STATUS',
            type: 'select',
            icon: <Tag className="h-3.5 w-3.5 text-[#50A970]" />,
            options: [
                { value: 'all', label: 'All Statuses' },
                ...STATUS_OPTIONS
            ],
            width: 'w-48'
        }
    ];

    // Advanced filter configurations
    const advancedFilters: FilterConfig[] = [
        {
            key: 'data_type',
            label: 'DATA TYPE',
            type: 'select',
            icon: <Database className="h-3.5 w-3.5 text-[#00FFFF]" />,
            placeholder: 'Select type',
            options: [
                { value: 'all', label: 'All Types' },
                { value: 'credential', label: 'Credential' },
                { value: 'breach', label: 'Breach' },
                { value: 'malware', label: 'Malware' },
                { value: 'domain', label: 'Domain' }
            ]
        },
        {
            key: 'source',
            label: 'SOURCE ENGINE',
            type: 'select',
            icon: <Globe className="h-3.5 w-3.5 text-purple-500" />,
            placeholder: 'Select source',
            options: [
                { value: 'all', label: 'All Sources' },
                { value: 'stealer', label: 'Stealer' },
                { value: 'breach', label: 'Breach' },
                { value: 'darkweb', label: 'Dark Web' },
                { value: 'osint', label: 'OSINT' }
            ]
        },
        {
            key: 'dateRange',
            label: 'TIME RANGE',
            type: 'select',
            icon: <Calendar className="h-3.5 w-3.5 text-orange-500" />,
            placeholder: 'Select range',
            options: [
                { value: 'all', label: 'All Time' },
                { value: '7d', label: 'Last 7 Days' },
                { value: '30d', label: 'Last 30 Days' },
                { value: '90d', label: 'Last 90 Days' }
            ]
        }
    ];

    const handleFilterChange = (key: string, value: any) => {
        onFilterChange(key as keyof SavedFilters, value);
    };

    return (
        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-5 mb-6 shadow-[0_0_30px_rgba(0,0,0,0.5)] font-mono cyber-filter-override">

            {/* Terminal Header for the Filter Block */}
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#1A1D24]">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#00FFFF]" />
                    <h3 className="text-[12px] font-bold text-white tracking-widest uppercase">
                        Query Parameters & Intercept Filters
                    </h3>
                </div>
                {hasActiveFilters && (
                    <div className="flex items-center gap-2 bg-[#50A970]/10 border border-[#50A970]/30 px-3 py-1 rounded-full">
                        <span className="h-2 w-2 rounded-full bg-[#50A970] animate-pulse"></span>
                        <span className="text-[9px] font-bold text-[#50A970] tracking-widest uppercase">Active Filters Applied</span>
                    </div>
                )}
            </div>

            {/* A wrapper to isolate the generic component from bleeding background colors */}
            <div className="filter-component-wrapper">
                <CustomFilter
                    searchValue={filters.search}
                    onSearchChange={(value) => onFilterChange('search', value)}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onResetFilters={onResetFilters}
                    filterConfigs={mainFilters}
                    advancedConfigs={advancedFilters}
                    showAdvanced={showAdvancedFilters}
                    onToggleAdvanced={onToggleAdvancedFilters}
                    hasActiveFilters={hasActiveFilters}
                />
            </div>

            {/* PRECISE CSS INJECTION */}
            <style dangerouslySetInnerHTML={{
                __html: `
                /* Safely clear ONLY the background of the immediate wrapper */
                .filter-component-wrapper > div {
                    background-color: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                }

                /* Base typography */
                .cyber-filter-override {
                    font-family: monospace !important;
                }

                /* FORCE ROW ALIGNMENT */
                .cyber-filter-override .flex {
                    align-items: center !important;
                }
                
                /* Labels above inputs */
                .cyber-filter-override label, 
                .cyber-filter-override .text-sm {
                    color: #94a3b8 !important;
                    font-size: 10px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    font-weight: bold !important;
                    margin-bottom: 6px !important;
                    display: block !important;
                }

                /* Inputs and Select Triggers */
                .cyber-filter-override input[type="text"],
                .cyber-filter-override input[type="search"], 
                .cyber-filter-override button[role="combobox"] {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    color: #00FFFF !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    border-radius: 6px !important;
                    height: 40px !important; 
                    box-sizing: border-box !important;
                    transition: all 0.2s ease !important;
                    margin: 0 !important;
                }

                /* Fix internal centering for the Select boxes */
                .cyber-filter-override button[role="combobox"] {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                }

                /* Focus states for inputs - Electric Cyan */
                .cyber-filter-override input:focus, 
                .cyber-filter-override button[role="combobox"]:focus,
                .cyber-filter-override input:focus-visible, 
                .cyber-filter-override button[role="combobox"]:focus-visible {
                    border-color: #00FFFF !important;
                    box-shadow: 0 0 15px rgba(0,255,255,0.15) !important;
                    outline: none !important;
                    ring: 0 !important;
                }

                /* Placeholders */
                .cyber-filter-override input::placeholder {
                    color: #334155 !important;
                    text-transform: uppercase !important;
                }

                /* Action Buttons (Reset, Advanced Toggle, Search) */
                .cyber-filter-override button:not([role="combobox"]) {
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    font-size: 10px !important;
                    font-weight: bold !important;
                    background-color: #12151C !important;
                    border: 1px solid #1A1D24 !important;
                    color: #94a3b8 !important;
                    border-radius: 6px !important;
                    height: 40px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    padding: 0 16px !important;
                    transition: all 0.2s ease !important;
                }

                .cyber-filter-override button:not([role="combobox"]):hover {
                    background-color: #161A23 !important;
                    color: #00FFFF !important;
                    border-color: #00FFFF !important;
                }

                /* Active state for the MORE filters button */
                .cyber-filter-override button:not([role="combobox"])[data-state="open"],
                .cyber-filter-override button:not([role="combobox"]):active {
                    background-color: #00FFFF !important;
                    color: #000000 !important;
                    border-color: #00FFFF !important;
                }


                /* ---------------------------------------------------
                   GLOBAL PORTAL OVERRIDES (For the Dropdown Menus) 
                   --------------------------------------------------- */
                
                [data-radix-popper-content-wrapper],
                div[role="presentation"] > div[role="listbox"],
                .radix-select-content {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    font-family: monospace !important;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.9) !important;
                    border-radius: 6px !important;
                    overflow: hidden !important;
                    z-index: 99999 !important;
                }

                /* FIX: Explicit solid background for the options */
                div[role="option"] {
                    background-color: #0A0B0E !important;
                    color: #94a3b8 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 32px 10px 14px !important; /* Leave room on right for SVG */
                    cursor: pointer !important;
                    transition: all 0.1s ease !important;
                    border-bottom: 1px solid #12151C !important;
                    position: relative !important;
                    display: flex !important;
                    align-items: center !important;
                    width: 100% !important;
                }
                
                div[role="option"]:last-child {
                    border-bottom: none !important;
                }

                div[role="option"]:hover,
                div[role="option"][data-highlighted] {
                    background-color: #161A23 !important;
                    color: #00FFFF !important;
                    outline: none !important;
                }

                div[role="option"][data-state="checked"] {
                    color: #50A970 !important;
                    background-color: #50A97010 !important;
                }

                /* FIX: Aggressively target the shadcn absolutely-positioned checkmark wrapper */
                div[role="option"] > span.absolute {
                    position: absolute !important;
                    left: auto !important; /* Eradicate the default left-2 class */
                    right: 12px !important; /* Pin perfectly to the right */
                    top: 50% !important;
                    transform: translateY(-50%) !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }

                /* Ensure the text span behaves normally */
                div[role="option"] > span:not(.absolute) {
                    position: static !important;
                    display: block !important;
                }
            `}} />
        </div>
    );
};

export default ThreatFilters;